/**
 * Piezas compartidas por las dos plantillas de importación de asignaciones
 * (relojes checadores y turnos).
 *
 * A propósito sin React ni antd: así el workbook y la validación se pueden generar
 * y revisar con un `node script.js`, sin levantar la app. Mismo criterio que
 * `plantillaIncidencias.js` y `plantillaInsumosExcel.js`.
 */

import * as XLSX from "xlsx";
import moment from "moment";

// ─── Normalización de texto ──────────────────────────────────────────────────

export const texto = (valor) => String(valor ?? "").trim();

// Sin acentos, sin espacios de más, en MAYÚSCULAS. Para cruzar nombres tal como
// vienen capturados. ̀-ͯ son las marcas diacríticas combinantes que deja
// NFD al descomponer (la Ñ incluida, que queda como N).
export const normalizarTexto = (valor) =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

// Para cruzar claves tipo "ASR - 12": se tira todo lo que no sea alfanumérico, de
// modo que "ASR - 12", "ASR-12" y "asr 12" caen en la misma llave.
export const llaveClave = (valor) => normalizarTexto(valor).replace(/[^A-Z0-9]/g, "");

// ─── Índices de cruce ────────────────────────────────────────────────────────

// Una llave que le queda a dos registros se marca como ambigua (null) en vez de
// quedarse con el primero: mejor pedirle al usuario que desambigüe que asignarle
// el reloj a la persona equivocada.
export const registrarLlave = (mapa, llave, valor) => {
  if (!llave) return;
  if (mapa.has(llave)) {
    const previo = mapa.get(llave);
    if (previo === null || String(previo._id) !== String(valor._id)) {
      mapa.set(llave, null);
    }
    return;
  }
  mapa.set(llave, valor);
};

// Busca en varios índices en orden de prioridad. Devuelve `ambiguo: true` cuando
// la llave existe pero le queda a más de un registro.
export const buscarEnIndices = (indices, llaves) => {
  for (let i = 0; i < indices.length; i += 1) {
    const llave = llaves[i];
    if (!llave) continue;
    if (!indices[i].has(llave)) continue;
    const encontrado = indices[i].get(llave);
    if (encontrado === null) return { encontrado: null, ambiguo: true };
    return { encontrado, ambiguo: false };
  }
  return { encontrado: null, ambiguo: false };
};

// ─── Empleados ───────────────────────────────────────────────────────────────

export const nombreEmpleado = (empleado) =>
  [empleado?.nombre, empleado?.apellidoPaterno, empleado?.apellidoMaterno]
    .map((parte) => texto(parte))
    .filter(Boolean)
    .join(" ");

// "ASR - 12". Se usa igual para empleados, relojes y turnos: los tres traen
// serie/numero.
export const claveSerieNumero = (doc) => {
  const serie = texto(doc?.serie);
  const numero = doc?.numero === undefined || doc?.numero === null ? "" : String(doc.numero);
  if (!serie && !numero) return "";
  if (!serie) return numero;
  if (!numero) return serie;
  return `${serie} - ${numero}`;
};

export const construirIndiceEmpleados = (empleados) => {
  const porClave = new Map();
  const porNombre = new Map();

  for (const empleado of empleados) {
    registrarLlave(porClave, llaveClave(claveSerieNumero(empleado)), empleado);

    // El número suelto, sin serie, porque es como suele venir capturado.
    if (empleado.numero !== undefined && empleado.numero !== null) {
      registrarLlave(porClave, llaveClave(String(empleado.numero)), empleado);
    }

    // Los dos órdenes habituales: en la BD el nombre vive partido en tres campos y
    // los archivos lo traen como una sola cadena, a veces con los apellidos
    // adelante.
    registrarLlave(porNombre, normalizarTexto(nombreEmpleado(empleado)), empleado);
    registrarLlave(
      porNombre,
      normalizarTexto(
        [empleado.apellidoPaterno, empleado.apellidoMaterno, empleado.nombre]
          .map((parte) => texto(parte))
          .filter(Boolean)
          .join(" "),
      ),
      empleado,
    );
  }

  return { porClave, porNombre };
};

// Resuelve el empleado de una fila. El número es la llave buena; el nombre es el
// respaldo para cuando la columna del número viene vacía.
export const resolverEmpleado = (indice, { numeroTexto, nombreTexto }) => {
  if (!numeroTexto && !nombreTexto) {
    return { errores: [{ campo: "Número Empleado", motivo: "está vacío (o pon el nombre)" }] };
  }

  if (numeroTexto) {
    // Se acepta también un nombre escrito en la columna del número: es un error de
    // captura frecuente y el cruce es igual de seguro (si le queda a dos
    // empleados, el índice lo marca ambiguo). Lo que NO se hace es ignorar un
    // número que no cruza y caerse al nombre de la otra columna: ahí sí podría
    // terminar asignándole el reloj a otra persona.
    const { encontrado, ambiguo } = buscarEnIndices(
      [indice.porClave, indice.porNombre],
      [llaveClave(numeroTexto), normalizarTexto(numeroTexto)],
    );
    if (ambiguo) {
      return {
        errores: [
          {
            campo: "Número Empleado",
            motivo: `"${numeroTexto}" le queda a más de un empleado; usa la serie completa (ej. "EMP - 12")`,
          },
        ],
      };
    }
    if (!encontrado) {
      return {
        errores: [{ campo: "Número Empleado", motivo: `"${numeroTexto}" no existe (revisa la hoja "Empleados")` }],
      };
    }
    return { empleado: encontrado };
  }

  const { encontrado, ambiguo } = buscarEnIndices([indice.porNombre], [normalizarTexto(nombreTexto)]);
  if (ambiguo) {
    return {
      errores: [
        {
          campo: "Empleado",
          motivo: `"${nombreTexto}" le queda a más de un empleado; llena "Número Empleado"`,
        },
      ],
    };
  }
  if (!encontrado) {
    return {
      errores: [
        {
          campo: "Empleado",
          motivo: `"${nombreTexto}" no cruzó con ningún empleado (clave normalizada: "${normalizarTexto(nombreTexto)}")`,
        },
      ],
    };
  }
  return { empleado: encontrado };
};

// ─── Catálogos con etiqueta (relojes, turnos) ────────────────────────────────

/**
 * Índice de un catálogo que en el Excel se escribe con una sola columna.
 *
 * Se registran cuatro llaves por registro para que el cruce funcione tanto con lo
 * que escribe la plantilla como con lo que un usuario teclea a mano: la clave
 * ("RC - 1"), el `sn` si lo tiene, el nombre suelto, y la etiqueta completa
 * ("RC - 1 - MB20 - Puerta principal"). Esa última es la que hace que la hoja
 * descargada vuelva a cruzar tal cual, sin que el usuario tenga que recortarla.
 */
export const construirIndiceEtiquetado = (items, etiqueta) => {
  const porClave = new Map();
  const porTexto = new Map();

  for (const item of items) {
    registrarLlave(porClave, llaveClave(claveSerieNumero(item)), item);
    if (item.sn) registrarLlave(porClave, llaveClave(item.sn), item);
    registrarLlave(porTexto, normalizarTexto(item.nombre), item);
    registrarLlave(porTexto, normalizarTexto(etiqueta(item)), item);
  }

  return { porClave, porTexto };
};

export const resolverEtiquetado = (indice, valor, { campo, hoja, ejemplo }) => {
  const valorTexto = texto(valor);
  if (!valorTexto) return { errores: [{ campo, motivo: "está vacío" }] };

  const { encontrado, ambiguo } = buscarEnIndices(
    [indice.porClave, indice.porTexto],
    [llaveClave(valorTexto), normalizarTexto(valorTexto)],
  );

  if (ambiguo) {
    return {
      errores: [
        { campo, motivo: `"${valorTexto}" le queda a más de un registro; usa la clave (ej. "${ejemplo}")` },
      ],
    };
  }
  if (!encontrado) {
    return { errores: [{ campo, motivo: `"${valorTexto}" no existe (revisa la hoja "${hoja}")` }] };
  }
  return { item: encontrado };
};

// ─── Estado ──────────────────────────────────────────────────────────────────

export const ESTADO_NOMBRE = { 0: "Cancelado", 1: "Activo", 2: "Deshabilitado" };

// normalizarTexto ya quitó el acento, así que "Sí" llega como "SI".
const ESTADOS_ACTIVO = new Set(["ACTIVO", "ACTIVA", "VIGENTE", "HABILITADO", "HABILITADA", "1", "SI"]);
const ESTADOS_DESHABILITADO = new Set([
  "DESHABILITADO",
  "DESHABILITADA",
  "INACTIVO",
  "INACTIVA",
  "BAJA",
  "2",
  "NO",
]);

// Devuelve 1, 2 o null si no se entiende.
export const parsearEstado = (valor) => {
  const clave = normalizarTexto(valor);
  if (ESTADOS_ACTIVO.has(clave)) return 1;
  if (ESTADOS_DESHABILITADO.has(clave)) return 2;
  return null;
};

// ─── Fechas ──────────────────────────────────────────────────────────────────

const DOS_DIGITOS = (n) => String(n).padStart(2, "0");

// "DD/MM/YYYY" para la plantilla. Se formatea en hora local del navegador a
// propósito: es lo que hace el resto del ERP (y lo que pinta la tabla de
// Asignación de Turnos), así que el día que se ve aquí es el mismo que se ve allá.
export const formatearFechaDia = (valor) => {
  if (!valor) return "";
  const m = moment(valor);
  return m.isValid() ? m.format("DD/MM/YYYY") : "";
};

/**
 * Lee una fecha de una celda de Excel y la devuelve como "YYYY-MM-DD", que es lo
 * que el backend normaliza con `parsearFechaDia`.
 *
 * Acepta las tres formas en que puede llegar:
 *  - texto "DD/MM/YYYY" (como la escribe la plantilla) o "YYYY-MM-DD",
 *  - un Date, si alguien leyó el archivo con `cellDates`,
 *  - un serial de Excel, que es lo que sale de una celda con formato de fecha
 *    cuando el archivo se lee SIN `cellDates` (el caso de ImportadorExcelGenerico).
 *
 * Devuelve null si no se entiende.
 */
export const parsearFechaExcel = (valor) => {
  if (valor === undefined || valor === null || valor === "") return null;

  if (valor instanceof Date) {
    if (Number.isNaN(valor.getTime())) return null;
    return `${valor.getFullYear()}-${DOS_DIGITOS(valor.getMonth() + 1)}-${DOS_DIGITOS(valor.getDate())}`;
  }

  if (typeof valor === "number") {
    if (!Number.isFinite(valor) || valor <= 0) return null;
    const partes = XLSX.SSF.parse_date_code(valor);
    if (!partes) return null;
    return `${partes.y}-${DOS_DIGITOS(partes.m)}-${DOS_DIGITOS(partes.d)}`;
  }

  const cadena = texto(valor);

  const iso = cadena.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (iso) {
    return `${iso[1]}-${DOS_DIGITOS(Number(iso[2]))}-${DOS_DIGITOS(Number(iso[3]))}`;
  }

  const latino = cadena.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (latino) {
    const dia = Number(latino[1]);
    const mes = Number(latino[2]);
    if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
    return `${latino[3]}-${DOS_DIGITOS(mes)}-${DOS_DIGITOS(dia)}`;
  }

  return null;
};

// ─── Construcción de hojas ───────────────────────────────────────────────────

// La hoja se arma con aoa_to_sheet y no con json_to_sheet para tener control
// total: los encabezados salen aunque no haya ni una fila, y cada valor se
// escribe como texto (que es lo que hace exacto el round-trip del PIN y de las
// fechas).
//
// Los valores vacíos van como null y no como "": aoa_to_sheet omite la celda de un
// null, mientras que un "" escribe una celda de texto vacía. Con celdas vacías
// reales, un renglón en blanco cuenta como renglón al leer el archivo de vuelta y
// llega al validador nada más para salir reportado como error.
export const hojaDeFilas = (columnas, filas, anchos) => {
  const aoa = [
    columnas,
    ...filas.map((fila) =>
      columnas.map((columna) => {
        const valor = fila[columna];
        return valor === undefined || valor === null || valor === "" ? null : valor;
      }),
    ),
  ];
  const hoja = XLSX.utils.aoa_to_sheet(aoa);
  hoja["!cols"] = columnas.map((columna, i) => ({ wch: anchos?.[i] ?? 18 }));
  return hoja;
};

// Hoja de catálogo de referencia (Empleados, Relojes, Turnos…).
export const hojaDeCatalogo = (columnas, filas, anchos) =>
  hojaDeFilas(
    columnas,
    filas.length > 0 ? filas : [{ [columnas[0]]: "(no hay registros)" }],
    anchos,
  );

export const hojaDeInstrucciones = (lineas) => {
  const hoja = XLSX.utils.aoa_to_sheet(lineas.map((linea) => [linea]));
  hoja["!cols"] = [{ wch: 110 }];
  return hoja;
};
